<template>
  <section class="map-card">
    <header class="map-card__header" :class="headerModifierClass">
      <div class="map-card__header-icon" :class="headerIconModifierClass">
        <i class="icon-star" aria-hidden="true"></i>
      </div>
      <div class="map-card__header-text">
        <h3 class="map-card__title">{{ $t("lang.meeting_recording.title") }}</h3>
        <p class="map-card__subtitle">{{ headerSubtitle }}</p>
      </div>
    </header>

    <section v-if="uiKind === 'scheduled'" class="map-card__body map-card__body--centered">
      <p class="map-card__status-title">{{ $t("lang.meeting_recording.scheduled_title") }}</p>
      <p class="map-card__status-desc">
        {{ $t("lang.meeting_recording.scheduled_desc_1") }}<br />
        {{ $t("lang.meeting_recording.scheduled_desc_2") }}
      </p>
    </section>

    <section v-else-if="uiKind === 'error'" class="map-card__body map-card__body--error">
      <div class="map-card__error-banner">
        <span class="map-card__error-icon-wrap">
          <i class="icon-exclamation-circle" aria-hidden="true"></i>
        </span>
        <div class="map-card__error-text">
          <p class="map-card__error-title">{{ $t("lang.meeting_recording.error_title") }}</p>
          <p class="map-card__error-desc">{{ $t("lang.meeting_recording.error_desc") }}</p>
        </div>
      </div>
    </section>

    <section v-else-if="uiKind === 'done'" class="map-card__body map-card__body--done">
      <div class="map-card__meta">
        <span class="map-card__meta-item">
          <i class="icon-calendar" aria-hidden="true"></i>
          {{ formattedDate }}
        </span>
        <span class="map-card__meta-item">
          <i class="icon-clock" aria-hidden="true"></i>
          {{ formattedTimeAndDuration }}
        </span>
        <span class="map-card__meta-item">
          <i class="icon-user" aria-hidden="true"></i>
          {{ prospectName }}
        </span>
      </div>

      <div class="map-card__engagement">
        <div class="map-card__engagement-head">
          <i class="icon-line-chart" aria-hidden="true"></i>
          <span>{{ $t("lang.meeting_recording.engagement_prospect") }}</span>
        </div>
        <div class="map-card__score-row">
          <span class="map-card__score-value">{{ displayScore }}</span>
          <span class="map-card__score-max">/ 100</span>
        </div>
        <div
          class="map-card__score-bar"
          role="progressbar"
          :aria-valuenow="displayScore"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="map-card__score-bar-fill"
            :class="scoreBarClass"
            :style="{ width: displayScore + '%' }"
          ></div>
        </div>
      </div>

      <div class="map-card__summary-block">
        <div class="map-card__summary-head">
          <i class="icon-file-text-o" aria-hidden="true"></i>
          <span>{{ $t("lang.meeting_recording.summary_heading") }}</span>
        </div>
        <p class="map-card__summary-text">{{ visibleSummary }}</p>
        <button
          v-if="showReadMoreToggle"
          type="button"
          class="map-card__read-more"
          @click="summaryExpanded = !summaryExpanded"
        >
          {{ summaryExpanded ? $t("lang.meeting_recording.read_less") : $t("lang.meeting_recording.read_more") }}
          <i :class="summaryExpanded ? 'icon-chevron-up' : 'icon-chevron-down'" aria-hidden="true"></i>
        </button>
      </div>

      <button type="button" class="map-card__cta" @click="onOpenAnalysis">
        <i class="icon-bar-chart" aria-hidden="true"></i>
        {{ $t("lang.meeting_recording.view_full_analysis") }}
      </button>
    </section>
  </section>
</template>

<script>
const SUMMARY_PREVIEW_LENGTH = 300;
const ERROR_STATUSES = ["deleted", "none_joined", "non_joined", "bot_removed"];

const DATE_LOCALE_MAP = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
  pt: "pt-PT",
};

export default {
  name: "MeetingAnalysisPreviewCard",

  props: {
    record: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      summaryExpanded: false,
    };
  },

  computed: {
    dateLocale() {
      return DATE_LOCALE_MAP[this.$i18n.locale] || "en-US";
    },

    normalizedStatus() {
      const raw = (this.record && this.record.recording_status) || "";
      return String(raw).toLowerCase().trim().replace(/-/g, "_").replace(/\s+/g, "_");
    },

    uiKind() {
      const s = this.normalizedStatus;
      if (s === "scheduled") return "scheduled";
      if (s === "done" || s === "completed") return "done";
      if (ERROR_STATUSES.includes(s)) return "error";
      return "unknown";
    },

    headerSubtitle() {
      if (this.uiKind === "scheduled") return this.$t("lang.meeting_recording.subtitle_scheduled");
      if (this.uiKind === "error") return this.$t("lang.meeting_recording.subtitle_error");
      return this.$t("lang.meeting_recording.subtitle_done");
    },

    headerModifierClass() {
      if (this.uiKind === "scheduled") return "map-card__header--scheduled";
      if (this.uiKind === "error") return "map-card__header--error";
      return "";
    },

    headerIconModifierClass() {
      if (this.uiKind === "scheduled") return "map-card__header-icon--scheduled";
      return "";
    },

    eventDateValue() {
      return (this.record && this.record.event_date) || null;
    },

    formattedDate() {
      const date = this.parseEventDate(this.eventDateValue);
      if (!date) return "—";
      const formatted = date.toLocaleDateString(this.dateLocale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },

    formattedTime() {
      const date = this.parseEventDate(this.eventDateValue);
      if (!date) return "—";
      return date.toLocaleTimeString(this.dateLocale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    formattedDuration() {
      const raw = this.record && this.record.meeting_duration;
      if (!raw) return null;
      const parts = String(raw).split(":").map((p) => parseInt(p, 10) || 0);
      let totalMinutes = 0;
      if (parts.length >= 3) {
        totalMinutes = parts[0] * 60 + parts[1] + (parts[2] > 0 ? 1 : 0);
      } else if (parts.length === 2) {
        totalMinutes = parts[0] * 60 + parts[1];
      } else if (parts.length === 1) {
        totalMinutes = parts[0];
      }
      if (totalMinutes <= 0) return null;
      if (totalMinutes < 60) {
        return this.$t("lang.meeting_recording.duration_minutes", { minutes: totalMinutes });
      }
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      if (mins > 0) {
        return this.$t("lang.meeting_recording.duration_hours_minutes", { hours, minutes: mins });
      }
      return this.$t("lang.meeting_recording.duration_hours", { hours });
    },

    formattedTimeAndDuration() {
      const time = this.formattedTime;
      const duration = this.formattedDuration;
      if (duration) return `${time} · ${duration}`;
      return time;
    },

    prospectName() {
      const name = [this.record.first_name, this.record.last_name].filter(Boolean).join(" ").trim();
      return name || "—";
    },

    displayScore() {
      const score = this.record && this.record.score;
      if (score == null || score === "") return 0;
      const n = Number(score);
      if (isNaN(n)) return 0;
      return Math.min(100, Math.max(0, Math.round(n)));
    },

    scoreBarClass() {
      if (this.displayScore >= 70) return "map-card__score-bar-fill--success";
      if (this.displayScore >= 40) return "map-card__score-bar-fill--warning";
      return "map-card__score-bar-fill--danger";
    },

    summaryFullText() {
      const summary = this.record && this.record.summary;
      if (summary == null || String(summary).trim() === "") {
        return this.$t("lang.meeting_recording.no_summary");
      }
      return String(summary).trim();
    },

    showReadMoreToggle() {
      return this.summaryFullText.length > SUMMARY_PREVIEW_LENGTH;
    },

    visibleSummary() {
      if (!this.showReadMoreToggle || this.summaryExpanded) {
        return this.summaryFullText;
      }
      return this.summaryFullText.slice(0, SUMMARY_PREVIEW_LENGTH).trim() + "…";
    },
  },

  watch: {
    "record.meeting_recording_id"() {
      this.summaryExpanded = false;
    },
  },

  methods: {
    parseEventDate(value) {
      if (!value) return null;
      const raw = String(value).trim();
      const isoLike = raw.includes("T") ? raw : raw.replace(" ", "T");
      const date = new Date(isoLike);
      return isNaN(date.getTime()) ? null : date;
    },

    onOpenAnalysis() {
      const meetingId = this.record && this.record.meeting_recording_id;
      this.$emit("open-analysis", { meetingId: meetingId != null ? Number(meetingId) : null });
    },
  },
};
</script>

<style scoped>
.map-card {
  --ol-success: #10b981;
  --ol-warning: #f59e0b;
  --ol-danger: #ef4444;
  --map-purple: #5b5fe9;
  --map-purple-light: #eeeffe;
  --map-border: #e5e7eb;
  --map-text: #1f2937;
  --map-text-muted: #6b7280;
  font-family: "Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, sans-serif;
  border: 1px solid var(--map-border);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.map-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--map-border);
  background: #fff;
}

.map-card__header--scheduled {
  background: #f5f6ff;
}

.map-card__header--error {
  background: #f5f6ff;
}

.map-card__header-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--map-purple);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.map-card__header-icon--scheduled {
  background: #fef3c7;
  color: #b45309;
}

.map-card__header-text {
  min-width: 0;
}

.map-card__title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 700;
  color: var(--map-text);
  line-height: 1.3;
}

.map-card__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--map-text-muted);
  line-height: 1.4;
}

.map-card__body {
  padding: 18px;
}

.map-card__body--centered {
  text-align: center;
  padding: 28px 24px 32px;
}

.map-card__body--done {
  padding: 16px 18px 18px;
}

.map-card__status-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--map-text);
}

.map-card__status-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--map-text-muted);
}

.map-card__body--error {
  padding: 0;
}

.map-card__error-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 16px 18px 18px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.map-card__error-icon-wrap {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b91c1c;
  font-size: 16px;
}

.map-card__error-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 700;
  color: #991b1b;
  line-height: 1.35;
}

.map-card__error-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #b91c1c;
}

.map-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 12px 14px;
  margin-bottom: 14px;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 13px;
  color: var(--map-text-muted);
}

.map-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.map-card__meta-item i {
  font-size: 14px;
  color: #9ca3af;
}

.map-card__engagement {
  padding: 14px 16px;
  margin-bottom: 14px;
  border: 1px solid var(--map-border);
  border-radius: 8px;
  background: #fff;
}

.map-card__engagement-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--map-text-muted);
}

.map-card__engagement-head i {
  font-size: 14px;
}

.map-card__score-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}

.map-card__score-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
  color: var(--map-text);
  letter-spacing: -0.02em;
}

.map-card__score-max {
  font-size: 14px;
  color: var(--map-text-muted);
}

.map-card__score-bar {
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  overflow: hidden;
}

.map-card__score-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.35s ease;
}

.map-card__score-bar-fill--success {
  background: linear-gradient(90deg, var(--ol-success), #34d399);
}

.map-card__score-bar-fill--warning {
  background: linear-gradient(90deg, var(--ol-warning), #fbbf24);
}

.map-card__score-bar-fill--danger {
  background: linear-gradient(90deg, var(--ol-danger), #f87171);
}

.map-card__summary-block {
  margin-bottom: 16px;
}

.map-card__summary-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--map-text-muted);
}

.map-card__summary-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
}

.map-card__read-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 600;
  color: var(--map-purple);
  cursor: pointer;
}

.map-card__read-more:hover {
  text-decoration: underline;
}

.map-card__read-more i {
  font-size: 12px;
}

.map-card__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: var(--map-purple);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.map-card__cta:hover {
  background: #4a4ed4;
}

.map-card__cta i {
  font-size: 15px;
}
</style>
